import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal, Show } from 'solid-js'
import { z } from 'zod'
import Alert from '~/components/Alert'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import FileInput from '~/components/FileInput'
import Form from '~/components/Form'
import Icon from '~/components/Icon'
import { useForm } from '~/hooks/useForm'
import type { FileInputFileType } from '~/types/FileInput'

const mockFiles: FileInputFileType[] = [
  {
    id: '1',
    file: new File([''], 'SystemForm-X.png', { type: 'image/png' }),
    name: 'SystemForm-X.png',
    size: 1887436,
    type: 'image/png',
    status: 'uploading',
    progress: 62
  },
  {
    id: '2',
    file: new File([''], 'Dashboard-V2.fig', { type: 'application/octet-stream' }),
    name: 'Dashboard-V2.fig',
    size: 4521984,
    type: 'application/octet-stream',
    status: 'complete',
    progress: 100
  },
  {
    id: '3',
    file: new File([''], 'reporte-ventas.pdf', { type: 'application/pdf' }),
    name: 'reporte-ventas.pdf',
    size: 892416,
    type: 'application/pdf',
    status: 'error',
    progress: 0,
    error: 'Archivo demasiado grande'
  },
  {
    id: '4',
    file: new File([''], 'logo-dark.svg', { type: 'image/svg+xml' }),
    name: 'logo-dark.svg',
    size: 24576,
    type: 'image/svg+xml',
    status: 'paused',
    progress: 35
  }
]

const filesSchema = z.object({
  adjuntos: z.array(z.any()).min(1, 'Adjunta al menos un archivo')
})

const FileInputPage: Component = () => {
  const [demoFiles, setDemoFiles] = createSignal<FileInputFileType[]>(mockFiles)
  const [formResult, setFormResult] = createSignal<string>('')

  const uploadForm = useForm({
    schema: filesSchema,
    onSubmit: async values => {
      const files = values.adjuntos as unknown as FileInputFileType[]
      setFormResult(`Recibidos ${files.length} archivo(s): ${files.map(f => f.name).join(', ')}`)
    }
  })

  return (
    <>
      <Title>FileInput - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">FileInput</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Componente de carga de archivos con soporte para drag & drop, vista previa de miniaturas, progreso en tiempo real y controles de pausa/cancelar.
          </p>
        </div>

        <div class="space-y-12">
          {/* Modos */}
          <Card>
            <Card.Header
              title="Modos"
              subtitle="El mismo componente con tres disparadores: campo, botón o zona de arrastre. Los tres aceptan soltar archivos encima."
              icon={<Icon icon="material-symbols-light:tune" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-8">
                <div class="space-y-3">
                  <Badge color="info" variant="soft" size="sm">
                    MODE="INPUT"
                  </Badge>
                  <FileInput mode="input" multiple hideFileList />
                </div>
                <div class="space-y-3">
                  <Badge color="secondary" variant="soft" size="sm">
                    MODE="BUTTON"
                  </Badge>
                  <FileInput mode="button" multiple hideFileList buttonLabel="Adjuntar documentos" />
                </div>
                <div class="space-y-3">
                  <Badge color="warning" variant="soft" size="sm">
                    MODE="DROPZONE" (POR DEFECTO)
                  </Badge>
                  <FileInput mode="dropzone" multiple hideFileList />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Dropzone Variants */}
          <Card>
            <Card.Header
              title="Zonas de Carga"
              subtitle="Tres variantes de dropzone para diferentes contextos de interfaz."
              icon={<Icon icon="material-symbols-light:cloud-upload-outline" class="text-info" size={24} />}
            />
            <Card.Body>
              <div class="space-y-8">
                <div class="space-y-3">
                  <Badge color="info" variant="soft" size="sm">
                    DASHED (DEFAULT)
                  </Badge>
                  <FileInput.Dropzone variant="dashed" />
                </div>
                <div class="space-y-3">
                  <Badge color="secondary" variant="soft" size="sm">
                    OUTLINED
                  </Badge>
                  <FileInput.Dropzone variant="outlined" />
                </div>
                <div class="space-y-3">
                  <Badge color="warning" variant="soft" size="sm">
                    DEFAULT (FILLED)
                  </Badge>
                  <FileInput.Dropzone variant="default" />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* File Items */}
          <Card>
            <Card.Header
              title="Estados de Archivo"
              subtitle="Cada archivo muestra su estado actual con indicadores visuales y controles contextualizados."
              icon={<Icon icon="material-symbols-light:description-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-3 p-6 bg-surface-muted rounded-2xl border border-border">
                <FileInput.FileItem file={mockFiles[0]} />
                <FileInput.FileItem file={mockFiles[1]} />
                <FileInput.FileItem file={mockFiles[2]} />
                <FileInput.FileItem file={mockFiles[3]} />
              </div>
            </Card.Body>
          </Card>

          {/* Progress Widget */}
          <Card>
            <Card.Header
              title="Progreso Global"
              subtitle="Widget flotante que monitorea el progreso de carga de múltiples archivos."
              icon={<Icon icon="material-symbols-light:trending-up" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-6">
                <div class="space-y-3">
                  <Badge color="info" variant="soft" size="sm">
                    CARGANDO (62%)
                  </Badge>
                  <FileInput.Progress totalFiles={7} completedFiles={4} progress={62} status="uploading" />
                </div>
                <div class="space-y-3">
                  <Badge color="warning" variant="soft" size="sm">
                    PAUSADO
                  </Badge>
                  <FileInput.Progress totalFiles={7} completedFiles={4} progress={62} status="paused" />
                </div>
                <div class="space-y-3">
                  <Badge color="success" variant="soft" size="sm">
                    COMPLETADO
                  </Badge>
                  <FileInput.Progress totalFiles={5} completedFiles={5} progress={100} status="complete" />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Toast */}
          <Card>
            <Card.Header
              title="Notificaciones"
              subtitle="Mensajes de confirmación para eventos de carga completados."
              icon={<Icon icon="material-symbols-light:notifications-outline" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-3">
                <FileInput.Toast message="Uploaded 5 files" variant="success" onClose={() => {}} />
                <FileInput.Toast message="Error al subir archivo" variant="error" onClose={() => {}} />
                <FileInput.Toast message="Procesando archivos..." variant="info" onClose={() => {}} />
              </div>
            </Card.Body>
          </Card>

          {/* Interactive Demo */}
          <Card>
            <Card.Header
              title="Demo Interactivo"
              subtitle="Prueba la carga de archivos con drag & drop o selección manual."
              icon={<Icon icon="material-symbols-light:play-circle-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <FileInput files={demoFiles()} onChange={setDemoFiles} maxFiles={10} maxSize={10485760} />
            </Card.Body>
          </Card>

          {/* Input style */}
          <Card>
            <Card.Header
              title="Estilo Input"
              subtitle="El mismo componente con disparador tipo input (compacto) y drag & drop activo. El panel de archivos se mantiene a la derecha."
              icon={<Icon icon="material-symbols-light:edit-document-outline" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-4">
                <FileInput appearance="input" files={demoFiles()} onChange={setDemoFiles} maxFiles={10} maxSize={10485760} />
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FileInput appearance="input" size="sm" files={[]} onChange={() => {}} />
                  <FileInput appearance="input" size="md" files={[]} onChange={() => {}} />
                  <FileInput appearance="input" size="lg" files={[]} onChange={() => {}} />
                </div>
                <p class="text-[10px] text-foreground-subtle italic">El size en la apariencia input controla la altura del disparador (40/48/56px).</p>
              </div>
            </Card.Body>
          </Card>

          {/* Sizes */}
          <Card>
            <Card.Header
              title="Tamaños"
              subtitle="Tres tamaños de dropzone para diferentes densidades de interfaz."
              icon={<Icon icon="material-symbols-light:aspect-ratio" class="text-indigo-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-6">
                <div class="space-y-3">
                  <Badge color="info" variant="soft" size="sm">
                    SMALL
                  </Badge>
                  <FileInput.Dropzone size="sm" />
                </div>
                <div class="space-y-3">
                  <Badge color="secondary" variant="soft" size="sm">
                    MEDIUM (DEFAULT)
                  </Badge>
                  <FileInput.Dropzone size="md" />
                </div>
                <div class="space-y-3">
                  <Badge color="warning" variant="soft" size="sm">
                    LARGE
                  </Badge>
                  <FileInput.Dropzone size="lg" />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Disabled */}
          <Card>
            <Card.Header
              title="Estado Deshabilitado"
              subtitle="Dropzone inactivo cuando no se permiten más cargas."
              icon={<Icon icon="material-symbols-light:block" class="text-danger" size={24} />}
            />
            <Card.Body>
              <FileInput.Dropzone disabled />
            </Card.Body>
          </Card>

          {/* Integración con Form */}
          <Card>
            <Card.Header
              title="Integración con Form"
              subtitle="FileInput participa en un <Form> con useForm: con la prop name el valor (lista de archivos) se recoge en form.values() y se valida con el schema."
              icon={<Icon icon="material-symbols-light:upload-file-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div class="p-8 bg-surface-muted rounded-2xl border border-border">
                  <Form controller={uploadForm} class="space-y-6">
                    <FileInput name="adjuntos" label="Archivos adjuntos" helperText="Requerido: al menos un archivo." files={demoFiles()} onChange={setDemoFiles} />
                    <Button type="submit" variant="contained" color="primary" disabled={!uploadForm.valid()}>
                      Enviar
                    </Button>
                  </Form>
                </div>

                <div class="space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-indigo-500">Valor recogido</h4>
                  <Show
                    when={formResult()}
                    fallback={
                      <div class="p-10 border-2 border-dashed border-border rounded-2xl flex flex-col items-center justify-center opacity-40">
                        <Icon icon="material-symbols-light:description-outline" size={48} />
                        <p class="text-sm mt-4">Envía el formulario para ver los datos</p>
                      </div>
                    }
                  >
                    <div class="animate-in zoom-in-95 duration-300">
                      <Alert variant="success" icon="material-symbols-light:check-circle-outline" title="Envío Exitoso">
                        {formResult()}
                      </Alert>
                    </div>
                  </Show>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas del componente FileInput."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto">
                <table class="w-full text-sm border-collapse">
                  <thead>
                    <tr class="border-b border-border">
                      <th class="text-left py-3 px-4 text-foreground-subtle font-semibold uppercase text-xs tracking-wider">Propiedad</th>
                      <th class="text-left py-3 px-4 text-foreground-subtle font-semibold uppercase text-xs tracking-wider">Tipo</th>
                      <th class="text-left py-3 px-4 text-foreground-subtle font-semibold uppercase text-xs tracking-wider">Default</th>
                      <th class="text-left py-3 px-4 text-foreground-subtle font-semibold uppercase text-xs tracking-wider">Descripción</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">variant</td>
                      <td class="py-3 px-4 text-foreground-subtle">'default' | 'outlined' | 'dashed'</td>
                      <td class="py-3 px-4 text-foreground-subtle">'dashed'</td>
                      <td class="py-3 px-4 text-foreground-muted">Estética visual del dropzone.</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">name</td>
                      <td class="py-3 px-4 text-foreground-subtle">string</td>
                      <td class="py-3 px-4 text-foreground-subtle">-</td>
                      <td class="py-3 px-4 text-foreground-muted">Nombre del campo: integra el FileInput con Form/useForm y con el FormData nativo.</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">label</td>
                      <td class="py-3 px-4 text-foreground-subtle">string</td>
                      <td class="py-3 px-4 text-foreground-subtle">-</td>
                      <td class="py-3 px-4 text-foreground-muted">Etiqueta visible sobre el componente.</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">error</td>
                      <td class="py-3 px-4 text-foreground-subtle">string</td>
                      <td class="py-3 px-4 text-foreground-subtle">-</td>
                      <td class="py-3 px-4 text-foreground-muted">Mensaje de error (p. ej. form.error('archivos')).</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">helperText</td>
                      <td class="py-3 px-4 text-foreground-subtle">string</td>
                      <td class="py-3 px-4 text-foreground-subtle">-</td>
                      <td class="py-3 px-4 text-foreground-muted">Texto de ayuda bajo el componente.</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">appearance</td>
                      <td class="py-3 px-4 text-foreground-subtle">'dropzone' | 'input'</td>
                      <td class="py-3 px-4 text-foreground-subtle">'dropzone'</td>
                      <td class="py-3 px-4 text-foreground-muted">Estilo del disparador. 'input' es compacto tipo campo; ambos permiten drag &amp; drop.</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">size</td>
                      <td class="py-3 px-4 text-foreground-subtle">'sm' | 'md' | 'lg'</td>
                      <td class="py-3 px-4 text-foreground-subtle">'md'</td>
                      <td class="py-3 px-4 text-foreground-muted">Tamaño del área de carga.</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">multiple</td>
                      <td class="py-3 px-4 text-foreground-subtle">boolean</td>
                      <td class="py-3 px-4 text-foreground-subtle">true</td>
                      <td class="py-3 px-4 text-foreground-muted">Permite seleccionar múltiples archivos.</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">maxFiles</td>
                      <td class="py-3 px-4 text-foreground-subtle">number</td>
                      <td class="py-3 px-4 text-foreground-subtle">-</td>
                      <td class="py-3 px-4 text-foreground-muted">Número máximo de archivos permitidos.</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">maxSize</td>
                      <td class="py-3 px-4 text-foreground-subtle">number</td>
                      <td class="py-3 px-4 text-foreground-subtle">-</td>
                      <td class="py-3 px-4 text-foreground-muted">Tamaño máximo por archivo en bytes.</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">disabled</td>
                      <td class="py-3 px-4 text-foreground-subtle">boolean</td>
                      <td class="py-3 px-4 text-foreground-subtle">false</td>
                      <td class="py-3 px-4 text-foreground-muted">Deshabilita la zona de carga.</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">files</td>
                      <td class="py-3 px-4 text-foreground-subtle">FileInputFileType[]</td>
                      <td class="py-3 px-4 text-foreground-subtle">[]</td>
                      <td class="py-3 px-4 text-foreground-muted">Lista de archivos controlada.</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">onChange</td>
                      <td class="py-3 px-4 text-foreground-subtle">(files: FileInputFileType[]) =&gt; void</td>
                      <td class="py-3 px-4 text-foreground-subtle">-</td>
                      <td class="py-3 px-4 text-foreground-muted">Callback al cambiar la lista de archivos.</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">onPause</td>
                      <td class="py-3 px-4 text-foreground-subtle">(fileId: string) =&gt; void</td>
                      <td class="py-3 px-4 text-foreground-subtle">-</td>
                      <td class="py-3 px-4 text-foreground-muted">Callback al pausar un archivo.</td>
                    </tr>
                    <tr class="border-b border-border/50">
                      <td class="py-3 px-4 font-mono text-primary font-medium">onResume</td>
                      <td class="py-3 px-4 text-foreground-subtle">(fileId: string) =&gt; void</td>
                      <td class="py-3 px-4 text-foreground-subtle">-</td>
                      <td class="py-3 px-4 text-foreground-muted">Callback al reanudar un archivo.</td>
                    </tr>
                    <tr>
                      <td class="py-3 px-4 font-mono text-primary font-medium">onCancel</td>
                      <td class="py-3 px-4 text-foreground-subtle">(fileId: string) =&gt; void</td>
                      <td class="py-3 px-4 text-foreground-subtle">-</td>
                      <td class="py-3 px-4 text-foreground-muted">Callback al cancelar un archivo.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default FileInputPage
